


(function () {
  'use strict';

  var SELECTORS = {
    container: '#builder-options',
    loading: '#builder-loading',
    form: '#builder-form',
    fieldsContainer: '#builder-fields-container',
    optionsData: '#builder-options-data',
    basePrice: '#builder-base-price',
    surchargesRow: '#builder-surcharges-row',
    surchargesTotal: '#builder-surcharges-total',
    totalPrice: '#builder-total-price',
    errors: '#builder-errors',
    errorMessage: '#builder-error-message',
    atcButton: '#builder-atc-button',
    atcText: '.builder-atc--text',
    atcSpinner: '.builder-atc--spinner',
    priceSummary: '#builder-price-summary'
  };

  var SECTION_DEFS = [
    {
      id: 'orientation',
      title: 'Playing Style & Orientation',
      intro: 'These choices define the fundamental shape and playing orientation of your guitar.',
      keys: ['hand_orientation', 'headstock_shape', 'headstock_binding']
    },
    {
      id: 'neck',
      title: 'Neck & Fretboard',
      intro: 'The neck is the part you hold. Its materials and construction directly affect how the guitar feels and sounds.',
      keys: ['neck_joint', 'neck_material', 'neck_nut_material', 'fretboard_material', 'neck_binding', 'fretboard_inlays']
    },
    {
      id: 'body',
      title: 'Body',
      intro: 'The body is the largest part of the guitar and the main driver of acoustic resonance and visual style.',
      keys: ['body_timber', 'body_material', 'body_top_veneer', 'body_binding']
    },
    {
      id: 'hardware',
      title: 'Hardware & Controls',
      intro: 'These are the physical components mounted to the guitar, the parts you touch and interact with every time you play.',
      keys: ['pickguard', 'cavity_covers', 'tone_volume_knobs']
    },
    {
      id: 'routing',
      title: 'Routing & Bridge',
      intro: 'These define the internal layout of your guitar. If unsure, Standard works for the vast majority of builds.',
      keys: ['tremolo', 'bridge_tailpiece_holes', 'pickup_routing', 'electronics_routing']
    },
    {
      id: 'notes',
      title: 'Additional Notes',
      intro: 'Anything else about your build: special requests, reference images, or questions for our team.',
      keys: ['order_notes']
    }
  ];

  var DESCRIPTIONS = {
    hand_orientation: 'Whether you play right-handed or left-handed. If you strum with your right hand, choose Right-Handed.',
    headstock_shape: 'The shape at the top of the neck where tuning pegs are mounted. Mostly visual, so choose what matches your style.',
    headstock_binding: 'A decorative trim strip around the headstock edge. Adds a premium finished look. Purely cosmetic.',
    neck_joint: 'How the neck attaches to the body. Bolt-on is easier to adjust and repair, and is recommended for first-time builders.',
    neck_material: 'The wood the neck is made from. Maple is bright and stable; mahogany is warmer and heavier.',
    neck_nut_material: 'The small slotted piece at the top of the fretboard that strings pass over. Bone gives slightly better sustain; plastic is perfectly functional.',
    fretboard_material: 'The wood your fingers press against when playing. Each feels noticeably different: rosewood is smooth, maple is bright, ebony is the hardest.',
    neck_binding: 'A decorative trim along the fretboard edge. Makes fret ends feel smoother. Optional cosmetic upgrade.',
    fretboard_inlays: 'Position markers on the fretboard that help you find your way while playing. Dots are classic; blocks and shapes are visual upgrades.',
    body_timber: 'The primary wood the body is made from. This is the single biggest tonal decision for a solid-body guitar.',
    body_material: 'The wood the body is made from. Different woods produce different resonance and weight characteristics.',
    body_top_veneer: 'A thin decorative wood layer on the front of the body. Purely cosmetic and does not change tone.',
    body_binding: 'A decorative trim around the body edge. Gives a premium finished appearance. Purely cosmetic.',
    pickguard: 'The plate on the guitar face that protects the finish from pick scratches. Also a significant visual element.',
    cavity_covers: 'The small plates on the back that cover electronics. Keeps dust out of the wiring.',
    tone_volume_knobs: 'The control knobs that adjust volume and tone. Different shapes are purely aesthetic, so all knobs do the same job.',
    bridge_tailpiece_holes: 'Pre-drilled mounting holes for the bridge hardware. Standard covers most common setups.',
    pickup_routing: 'Cavities cut into the body for pickups. Standard fits the most common configurations.',
    electronics_routing: 'Internal channels connecting pickups to controls. Standard covers all common wiring layouts.',
    tremolo: 'A tremolo system lets you use a whammy bar to change pitch. This choice affects bridge routing.',
    order_notes: 'Tell us anything else about your build: hardware you already own, special requests, or questions for our team.'
  };

  var SMART_DEFAULTS = {
    pickguard: 'None',
    headstock_binding: 'None',
    neck_nut_material: 'White Plastic',
    neck_binding: 'None',
    body_top_veneer: 'None',
    body_binding: 'None',
    cavity_covers: 'None',
    bridge_tailpiece_holes: 'Standard',
    pickup_routing: 'Standard',
    electronics_routing: 'Standard'
  };

  var SURCHARGE_VARIANTS = {
    5:  43727397486726,
    10: 43727397519494,
    15: 43727397552262,
    20: 43727397585030,
    25: 43727397617798,
    30: 43727397650566,
    35: 43727397683334,
    40: 43727397716102
  };

  // Whole-dollar display (founder 2026-10-17): $399 USD, real cents kept ($414.50 USD), thousands separated.
  function formatMoney(cents) {
    var s = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + (s.slice(-3) === '.00' ? s.slice(0, -3) : s) + ' USD';
  }

  function Builder() {
    this.container = document.querySelector(SELECTORS.container);
    if (!this.container) return;

    this.productId = this.container.dataset.productId;
    this.variantId = this.container.dataset.variantId;
    this.basePrice = parseInt(this.container.dataset.productPrice, 10) || 0;
    this.optionSetHandle = this.container.dataset.optionSet;
    this.assetBase = this.container.dataset.assetBase || '';
    this.productTitle = this.container.dataset.productTitle || 'Guitar Kit';
    this.selections = {};
    this.surchargeMap = {};
    this.optionsData = null;
    this.openSection = 0;
    this.isSubmitting = false;

    this.init();
  }

  Builder.prototype.init = function () {
    var dataEl = document.querySelector(SELECTORS.optionsData);
    if (!dataEl) { this.showError('Option set data not found.'); return; }

    try {
      this.optionsData = JSON.parse(dataEl.textContent);
    } catch (e) {
      this.showError('Failed to parse option data.'); return;
    }

    this.storageKey = 'builder_state_' + (this.container.dataset.productHandle || this.productId);
    this._initializing = true;
    this.sections = this.buildSections();
    this.renderBuilder();
    this.applySmartDefaults();
    this._initializing = false;
    this.restoreState();
    this.updatePriceSummary();
    this.updateProgress();
    this.updateSectionSummaries();
    this.bindEvents();

    var loading = document.querySelector(SELECTORS.loading);
    if (loading) loading.style.display = 'none';
    var form = document.querySelector(SELECTORS.form);
    if (form) form.style.display = '';
  };

  Builder.prototype.saveState = function () {
    if (this._initializing) return;
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify({
        selections: this.selections,
        surchargeMap: this.surchargeMap
      }));
    } catch (e) {  }
  };

  Builder.prototype.restoreState = function () {
    var saved;
    try {
      var raw = sessionStorage.getItem(this.storageKey);
      if (raw) saved = JSON.parse(raw);
    } catch (e) { return; }
    if (!saved || !saved.selections) return;

    var self = this;
    var hasRestoredAny = false;

    Object.keys(saved.selections).forEach(function (key) {
      var value = saved.selections[key];
      if (!value || value === '') return;

      if (self.selections[key] === value) {
        if (saved.surchargeMap[key]) self.surchargeMap[key] = saved.surchargeMap[key];
        return;
      }

      var select = document.querySelector('.builder-field__select[data-option-key="' + key + '"]');
      if (select) {
        for (var i = 0; i < select.options.length; i++) {
          if (select.options[i].value === value) {
            select.selectedIndex = i;
            self.handleSelection(select);
            hasRestoredAny = true;
            break;
          }
        }
        return;
      }

      var radios = document.querySelectorAll('[data-option-key="' + key + '"]');
      radios.forEach(function (r) {
        if ((r.matches('.builder-swatch__input') || r.matches('.builder-radio__input')) && r.value === value) {
          r.checked = true;
          self.handleSelection(r);
          hasRestoredAny = true;
        }
      });
    });

    if (saved.selections.order_notes) {
      var ta = document.querySelector('.builder-option__textarea[data-option-key="order_notes"]');
      if (ta) ta.value = saved.selections.order_notes;
    }
  };

  Builder.prototype.buildSections = function () {
    var options = this.optionsData.options;
    var optionMap = {};
    for (var i = 0; i < options.length; i++) {
      optionMap[options[i].key] = options[i];
    }

    var sections = [];
    for (var s = 0; s < SECTION_DEFS.length; s++) {
      var def = SECTION_DEFS[s];
      var sectionOptions = [];
      for (var k = 0; k < def.keys.length; k++) {
        if (optionMap[def.keys[k]]) {
          sectionOptions.push(optionMap[def.keys[k]]);
        }
      }
      if (sectionOptions.length > 0) {
        sections.push({ id: def.id, title: def.title, intro: def.intro, options: sectionOptions });
      }
    }
    return sections;
  };

  Builder.prototype.renderBuilder = function () {
    var container = document.querySelector(SELECTORS.fieldsContainer);
    if (!container) return;

    var html = '';
    html += '<div class="builder__header">';
    html += '<h2 class="builder__title">Customize Your ' + this.escapeHtml(this.productTitle) + '</h2>';
    html += '<p class="builder__subtitle">Build your dream guitar step by step. Each section guides you through the choices. Take your time, there are no wrong answers.</p>';
    html += '</div>';


    for (var s = 0; s < this.sections.length; s++) {
      var section = this.sections[s];
      var isOpen = s === this.openSection;
      html += '<div class="builder-section' + (isOpen ? ' builder-section--open' : '') + '" data-section-index="' + s + '">';
      html += '<div class="builder-section__header" data-toggle-section="' + s + '">';
      html += '<div class="builder-section__number"><span>' + (s + 1) + '</span></div>';
      html += '<div class="builder-section__title-group">';
      html += '<div class="builder-section__title">' + this.escapeHtml(section.title) + '</div>';
      html += '<div class="builder-section__summary" id="section-summary-' + s + '">' + this.escapeHtml(section.intro.substring(0, 60)) + '</div>';
      html += '</div>';
      html += '<svg class="builder-section__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 9l6 6 6-6"/></svg>';
      html += '</div>';

      html += '<div class="builder-section__body">';
      for (var o = 0; o < section.options.length; o++) {
        html += this.renderField(section.options[o]);
      }
      html += '</div></div>';
    }

    html += '<div class="builder-delivery-notice">';
    html += '<div class="builder-delivery-notice__icon">&#9432;</div>';
    html += '<div class="builder-delivery-notice__content">';
    html += '<strong>Estimated Delivery: ~8 months</strong> from the time of your order, which includes both production and shipping.';
    html += '<br><br>If you wish to cancel your Custom Shop order, please do so within 5 days to avoid a $150 cancellation fee. ';
    html += '<a href="/policies/refund-policy" target="_blank">Learn more about our Refund Policy</a>.';
    html += '</div>';
    html += '</div>';

    container.innerHTML = html;
  };

  Builder.prototype.renderField = function (option) {
    var hasDefault = SMART_DEFAULTS.hasOwnProperty(option.key);
    var isRequired = option.required && !hasDefault;
    var description = DESCRIPTIONS[option.key] || option.help_text || '';

    var html = '<div class="builder-field" data-option-key="' + option.key + '">';
    html += '<div class="builder-field__label">' + this.escapeHtml(option.name);
    if (isRequired) html += '<span class="builder-field__required">*</span>';
    if (hasDefault) html += '<span class="builder-field__default-badge">Defaults to ' + this.escapeHtml(SMART_DEFAULTS[option.key]) + '</span>';
    html += '</div>';

    if (description) html += '<div class="builder-field__description">' + this.escapeHtml(description) + '</div>';

    switch (option.type) {
      case 'dropdown': html += this.renderDropdown(option); break;
      case 'swatch': html += this.renderSwatchCards(option); break;
      case 'radio': html += this.renderRadios(option); break;
      case 'textarea': html += this.renderTextarea(option); break;
    }

    if (option.type !== 'textarea') {
      html += '<input type="hidden" name="properties[' + this.escapeAttr(option.name) + ']" class="builder-option__value" data-option-key="' + option.key + '" value="">';
    }

    html += '</div>';
    return html;
  };

  Builder.prototype.renderDropdown = function (option) {
    var html = '<select class="builder-field__select" data-option-key="' + option.key + '">';
    html += '<option value="">-- Choose --</option>';
    for (var i = 0; i < option.values.length; i++) {
      var val = option.values[i];
      var surchargeText = val.surcharge > 0 ? ' [+$' + val.surcharge.toFixed(2) + ']' : '';
      html += '<option value="' + this.escapeAttr(val.label) + '" data-surcharge="' + val.surcharge + '">' + this.escapeHtml(val.label) + surchargeText + '</option>';
    }
    html += '</select>';
    return html;
  };

  Builder.prototype.renderSwatchCards = function (option) {
    var html = '<div class="builder-field__swatches" data-option-key="' + option.key + '">';
    for (var i = 0; i < option.values.length; i++) {
      var val = option.values[i];
      var inputId = 'swatch-' + option.key + '-' + i;
      var priceText = val.surcharge > 0 ? '+$' + val.surcharge.toFixed(2) : 'Included';
      var priceClass = val.surcharge > 0 ? ' builder-swatch__price--surcharge' : '';

      html += '<label class="builder-swatch" for="' + inputId + '">';
      html += '<input type="radio" name="swatch_' + option.key + '" id="' + inputId + '" value="' + this.escapeAttr(val.label) + '" data-surcharge="' + val.surcharge + '" data-option-key="' + option.key + '" class="builder-swatch__input">';

      if (val.image) {
        html += '<span class="builder-swatch__image"><img src="' + this.escapeAttr(this.assetBase + val.image) + '" alt="' + this.escapeAttr(val.label) + '" loading="lazy" onerror="this.parentElement.classList.add(\'builder-swatch__image--fallback\')"></span>';
      } else {
        html += '<span class="builder-swatch__color"></span>';
      }

      html += '<span class="builder-swatch__info"><span class="builder-swatch__name">' + this.escapeHtml(val.label) + '</span><span class="builder-swatch__price' + priceClass + '">' + priceText + '</span></span>';
      html += '</label>';
    }
    html += '</div>';
    return html;
  };

  Builder.prototype.renderRadios = function (option) {
    var html = '<div class="builder-option__radios" data-option-key="' + option.key + '">';
    for (var i = 0; i < option.values.length; i++) {
      var val = option.values[i];
      var surchargeText = val.surcharge > 0 ? ' [+$' + val.surcharge.toFixed(2) + ']' : '';
      var inputId = 'radio-' + option.key + '-' + i;
      html += '<label class="builder-radio" for="' + inputId + '"><input type="radio" name="radio_' + option.key + '" id="' + inputId + '" value="' + this.escapeAttr(val.label) + '" data-surcharge="' + val.surcharge + '" data-option-key="' + option.key + '" class="builder-radio__input"><span class="builder-radio__label">' + this.escapeHtml(val.label) + surchargeText + '</span></label>';
    }
    html += '</div>';
    return html;
  };

  Builder.prototype.renderTextarea = function (option) {
    return '<textarea class="builder-field__textarea builder-option__textarea" name="properties[' + this.escapeAttr(option.name) + ']" data-option-key="' + option.key + '" placeholder="Enter your requests here, separated by commas or new lines" maxlength="10000" rows="4"></textarea>';
  };

  Builder.prototype.applySmartDefaults = function () {
    var self = this;
    Object.keys(SMART_DEFAULTS).forEach(function (key) {
      var defaultVal = SMART_DEFAULTS[key];
      var select = document.querySelector('.builder-field__select[data-option-key="' + key + '"]');
      if (select) {
        for (var i = 0; i < select.options.length; i++) {
          if (select.options[i].value === defaultVal) {
            select.selectedIndex = i;
            self.handleSelection(select);
            break;
          }
        }
      }
    });
  };

  Builder.prototype.bindEvents = function () {
    var self = this;
    var form = document.querySelector(SELECTORS.form);
    if (!form) return;

    form.addEventListener('change', function (e) {
      if (e.target.matches('.builder-field__select')) self.handleSelection(e.target);
      if (e.target.matches('.builder-swatch__input, .builder-radio__input')) self.handleSelection(e.target);
    });

    form.addEventListener('input', function (e) {
      if (e.target.matches('.builder-option__textarea')) {
        self.selections[e.target.dataset.optionKey] = e.target.value;
        self.updateProgress();
        self.saveState();
      }
    });

    form.addEventListener('click', function (e) {
      var header = e.target.closest('[data-toggle-section]');
      if (header) self.toggleSection(parseInt(header.dataset.toggleSection, 10));
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      self.handleSubmit();
    });
  };

  Builder.prototype.toggleSection = function (index) {
    var sections = document.querySelectorAll('.builder-section');
    var wasOpen = sections[index] && sections[index].classList.contains('builder-section--open');
    for (var i = 0; i < sections.length; i++) {
      if (i === index) sections[i].classList.toggle('builder-section--open');
      else sections[i].classList.remove('builder-section--open');
    }
    this.openSection = index;
    if (!wasOpen && sections[index]) {
      setTimeout(function () {
        sections[index].scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  };

  Builder.prototype.handleSelection = function (input) {
    var optionKey = input.dataset.optionKey;
    var value = input.value;
    var surcharge = 0;

    if (input.tagName === 'SELECT') {
      surcharge = parseFloat(input.options[input.selectedIndex].dataset.surcharge) || 0;
    } else {
      surcharge = parseFloat(input.dataset.surcharge) || 0;
    }

    this.selections[optionKey] = value;
    this.surchargeMap[optionKey] = surcharge;

    var hiddenInput = document.querySelector('.builder-option__value[data-option-key="' + optionKey + '"]');
    if (hiddenInput) hiddenInput.value = value;

    if (input.matches('.builder-swatch__input')) {
      var swatchContainer = input.closest('.builder-field__swatches');
      if (swatchContainer) {
        swatchContainer.querySelectorAll('.builder-swatch').forEach(function (s) { s.classList.remove('builder-swatch--active'); });
      }
      var activeSwatch = input.closest('.builder-swatch');
      if (activeSwatch) activeSwatch.classList.add('builder-swatch--active');
    }

    this.updatePriceSummary();
    this.updateProgress();
    this.updateSectionSummaries();
    this.saveState();
    this.clearErrors();
  };

  Builder.prototype.updatePriceSummary = function () {
    var totalSurcharge = 0;
    for (var key in this.surchargeMap) totalSurcharge += this.surchargeMap[key];
    var surchargesCents = totalSurcharge * 100;
    var totalCents = this.basePrice + surchargesCents;

    var surchargesRow = document.querySelector(SELECTORS.surchargesRow);
    var surchargesTotal = document.querySelector(SELECTORS.surchargesTotal);
    var totalPrice = document.querySelector(SELECTORS.totalPrice);

    if (surchargesRow) surchargesRow.style.display = totalSurcharge > 0 ? '' : 'none';
    if (surchargesTotal) surchargesTotal.textContent = '+' + formatMoney(surchargesCents);
    if (totalPrice) totalPrice.innerHTML = '<strong>' + formatMoney(totalCents) + '</strong>';
  };

  Builder.prototype.updateProgress = function () {
    var sectionEls = document.querySelectorAll('.builder-section');

    for (var s = 0; s < this.sections.length; s++) {
      var section = this.sections[s];
      var allFilled = true;
      var hasNonTextarea = false;
      var hasTextareaContent = false;
      for (var o = 0; o < section.options.length; o++) {
        if (section.options[o].type === 'textarea') {
          var ta = document.querySelector('.builder-option__textarea[data-option-key="' + section.options[o].key + '"]');
          if (ta && ta.value.trim() !== '') hasTextareaContent = true;
          continue;
        }
        hasNonTextarea = true;
        if (!this.selections[section.options[o].key] || this.selections[section.options[o].key] === '') { allFilled = false; break; }
      }
      if (!hasNonTextarea) allFilled = true;

      if (sectionEls[s]) {
        sectionEls[s].classList.toggle('builder-section--done', allFilled);
        var numEl = sectionEls[s].querySelector('.builder-section__number');
        if (numEl) numEl.innerHTML = allFilled ? '\u2713' : '<span>' + (s + 1) + '</span>';
      }
    }
  };

  Builder.prototype.updateSectionSummaries = function () {
    for (var s = 0; s < this.sections.length; s++) {
      var section = this.sections[s];
      var parts = [];
      for (var o = 0; o < section.options.length; o++) {
        var val = this.selections[section.options[o].key];
        if (val && val !== '' && val !== 'None' && val !== 'Standard') parts.push(val);
      }
      var el = document.getElementById('section-summary-' + s);
      if (el) el.textContent = parts.length > 0 ? parts.join(', ') : section.intro.substring(0, 60);
    }
  };

  Builder.prototype.validate = function () {
    var errors = [];
    var options = this.optionsData.options;
    for (var i = 0; i < options.length; i++) {
      if (!options[i].required || options[i].type === 'textarea') continue;
      if (!this.selections[options[i].key] || this.selections[options[i].key] === '') errors.push(options[i].name + ' is required');
    }
    return errors;
  };

  Builder.prototype.handleSubmit = function () {
    if (this.isSubmitting) return;

    var self = this;
    var errors = this.validate();
    if (errors.length > 0) { this.showError('Please select: ' + errors.join(', ')); return; }

    this.isSubmitting = true;
    this.setSubmitState(true);

    var missingSurcharges = [];
    for (var sk in this.surchargeMap) {
      if (this.surchargeMap[sk] > 0 && !SURCHARGE_VARIANTS[this.surchargeMap[sk]]) {
        missingSurcharges.push(this.getOptionNameByKey(sk) + ' (+$' + this.surchargeMap[sk].toFixed(2) + ')');
      }
    }
    if (missingSurcharges.length > 0) {
      this.isSubmitting = false;
      this.setSubmitState(false);
      this.showError('Unable to process upgrade pricing for: ' + missingSurcharges.join(', ') + '. Please contact us.');
      return;
    }

    var items = [];
    var mainItem = { id: parseInt(this.variantId, 10), quantity: 1, properties: { '_builder_qty_lock': '1' } };

    var options = this.optionsData.options;
    for (var i = 0; i < options.length; i++) {
      var value = '';
      if (options[i].type === 'textarea') {
        var ta = document.querySelector('.builder-option__textarea[data-option-key="' + options[i].key + '"]');
        value = ta ? ta.value : '';
      } else {
        value = this.selections[options[i].key] || '';
      }
      if (value) {
        var propSurcharge = this.surchargeMap[options[i].key] || 0;
        mainItem.properties[options[i].name] = propSurcharge > 0 ? value + ' (+$' + propSurcharge.toFixed(2) + ')' : value;
      }
    }
    items.push(mainItem);

    for (var key in this.surchargeMap) {
      var amount = this.surchargeMap[key];
      if (amount > 0) {
        var optName = this.getOptionNameByKey(key);
        var optValue = this.selections[key];
        items.push({ id: SURCHARGE_VARIANTS[amount], quantity: 1, properties: { 'Option': optName + ': ' + optValue + ' (+$' + amount.toFixed(2) + ')', '_builder_parent': this.container.dataset.productHandle, '_builder_option': optName, '_builder_value': optValue, '_builder_qty_lock': '1' } });
      }
    }

    fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: items }) })
      .then(function (r) { if (!r.ok) return r.json().then(function (d) { throw new Error(d.description || 'Failed to add to cart'); }); return r.json(); })
      .then(function () { window.location.href = '/cart'; })
      .catch(function (err) { self.isSubmitting = false; self.setSubmitState(false); self.showError(err.message || 'Failed to add to cart.'); });
  };

  Builder.prototype.setSubmitState = function (loading) {
    var btn = document.querySelector(SELECTORS.atcButton);
    if (!btn) return;
    var text = btn.querySelector(SELECTORS.atcText);
    var spinner = btn.querySelector(SELECTORS.atcSpinner);
    btn.disabled = loading;
    if (text) text.style.display = loading ? 'none' : '';
    if (spinner) spinner.style.display = loading ? '' : 'none';
  };

  Builder.prototype.showError = function (msg) {
    var el = document.querySelector(SELECTORS.errors);
    var msgEl = document.querySelector(SELECTORS.errorMessage);
    if (el && msgEl) { msgEl.textContent = msg; el.style.display = ''; }
    this.showToast(msg);
  };

  Builder.prototype.showToast = function (msg) {
    var existing = document.querySelector('.builder-toast');
    if (existing) existing.remove();
    var toast = document.createElement('div');
    toast.className = 'builder-toast';
    toast.textContent = msg;
    document.body.appendChild(toast);
    requestAnimationFrame(function () { toast.classList.add('builder-toast--visible'); });
    setTimeout(function () {
      toast.classList.remove('builder-toast--visible');
      setTimeout(function () { toast.remove(); }, 300);
    }, 4000);
  };

  Builder.prototype.clearErrors = function () {
    var el = document.querySelector(SELECTORS.errors);
    if (el) el.style.display = 'none';
  };

  Builder.prototype.getOptionNameByKey = function (key) {
    if (!this.optionsData) return key;
    for (var i = 0; i < this.optionsData.options.length; i++) {
      if (this.optionsData.options[i].key === key) return this.optionsData.options[i].name;
    }
    return key;
  };

  Builder.prototype.escapeHtml = function (str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  };

  Builder.prototype.escapeAttr = function (str) {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { new Builder(); });
  } else {
    new Builder();
  }
})();
